import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogTrigger, DialogClose } from '@/components/ui/dialog'
import { useUnsavedChangesPrompt } from '@/hooks/use-unsaved-changes'
import { toast } from 'sonner'
import { TOASTS } from '@/data/toast-messages'
import { ToastMessage } from '@/components/toast-message'
import CategoryForm from './category-form'
import { Divider } from '@/components/divider'
import { Icons } from '@/theme/Images'
import RulesDialogHeader from '../dialog-header/rules-dialog-header'
import ConfirmationDialog from '../confirmation-dialog/confirmation-dialog'
import { initialValues, validationSchema } from './category-config'
import { useFormik } from 'formik'
import { useCreateClass } from '@/pages/hooks/use-rewards-class'
import { useAuth } from '@/contexts/auth.context'

const CreateCategories = () => {
  const { mutate: createClass } = useCreateClass()
  //Helps in managing unsaved changes*
  const {
    setUnsavedChanges, //sets a flag indicating whether there are unsaved changes
    showConfirmation, //displays a confirmation prompt-
    handleConfirm, //handles the confirmation--
    handleCancel, //handles the cancellation
    promptIfUnsaved, //checks if there are unsaved changes & prompts  user to confirm or cancel
  } = useUnsavedChangesPrompt(() => {
    setOpen(false)
  })

  const { user } = useAuth()

  const [open, setOpen] = useState(false)

  const [categoryDescription, setCategoryDescription] = useState('')
  const [checkedMCCItems, setCheckedMCCItems] = useState({})
  const [checkedMIDItems, setCheckedMIDItems] = useState({})
  const [checkedCIDItems, setCheckedCIDItems] = useState({})
  const [searchTermMCC, setSearchTermMCC] = useState('')
  const [searchTermMID, setSearchTermMID] = useState('')
  const [searchTermCID, setSearchTermCID] = useState('')

  const handleCategoryDescriptionChange = value => {
    setCategoryDescription(value)
    setUnsavedChanges(true)
  }

  //Updates the checked items for MCC, MID, or CID based on the user's selection
  const handleCheckboxChange = (type, index) => {
    const setCheckedItems = {
      MCC: setCheckedMCCItems,
      MID: setCheckedMIDItems,
      CHAIN: setCheckedCIDItems,
    }

    setCheckedItems[type](prev => ({
      ...prev,
      [index]: !prev[index],
    }))
    setUnsavedChanges(true)
  }

  const handleSearchChangeMCC = value => {
    setSearchTermMCC(value)
    setUnsavedChanges(true)
  }

  const handleSearchChangeMID = value => {
    setSearchTermMID(value)
    setUnsavedChanges(true)
  }

  const handleSearchChangeCID = value => {
    setSearchTermCID(value)
    setUnsavedChanges(true)
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      const transformedDescription = values.description.toUpperCase().split(' ').join('_')
      const transformedValues = {
        rewardClass: transformedDescription,
        description: values.description,
        mccList: Object.keys(checkedMCCItems).filter(key => checkedMCCItems[key]),
        midList: Object.keys(checkedMIDItems).filter(key => checkedMIDItems[key]),
        chainList: Object.keys(checkedCIDItems).filter(key => checkedCIDItems[key]),
        createdBy: user?.email,
      }

      createClass(transformedValues, {
        onSuccess: response => {
          if (!response?.rewardClass) {
            setOpen(false)
            console.error('Failed to create category: Missing rewardClass in response.')
            const updatedToast = {
              ...TOASTS.CREATE_CATEGORY.CATEGORY_CREATE_FAILURE,
              description: TOASTS.CREATE_CATEGORY.CATEGORY_CREATE_FAILURE.description.replace(
                '[Category Name]',
                formik.values?.description
              ),
            }
            toast.custom(() => <ToastMessage {...updatedToast} />)
            return
          }
          setOpen(false)
          formik.resetForm()
          resetCheckboxes()
          resetSearchTerms()
          toast.custom(() => <ToastMessage {...TOASTS.CREATE_CATEGORY.CATEGORY_CREATE_SUCCESS} />)
        },
        onError: error => {
          console.error('Error creating category:', error)
          const updatedToast = {
            ...TOASTS.CREATE_CATEGORY.CATEGORY_CREATE_FAILURE,
            description: TOASTS.CREATE_CATEGORY.CATEGORY_CREATE_FAILURE.description.replace(
              '[Category Name]',
              formik.values?.description
            ),
          }
          toast.custom(() => <ToastMessage {...updatedToast} />)
        },
      })
    },
  })

  const resetCheckboxes = () => {
    setCheckedMCCItems({})
    setCheckedMIDItems({})
    setCheckedCIDItems({})
  }

  const resetSearchTerms = () => {
    setSearchTermMCC('')
    setSearchTermMID('')
    setSearchTermCID('')
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) {
            promptIfUnsaved() // Prompt if there are unsaved changes
          } else {
            setUnsavedChanges(false) // Reset unsaved changes if dialog is opened
            formik.resetForm()
          }
        }}
      >
        <DialogTrigger asChild>
          <Button
            className="bg-brand-600 hover:bg-brand-700 md:w-auto h-[36px] text-sm text-white"
            onClick={() => setOpen(true)}
          >
            <img src={Icons.plusIcon} alt="icon" height={11.67} width={11.67} />
            <span className="font-semibold text-sm ml-2">Add Category</span>
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-[880px] h-[98vh] overflow-y-auto overflow-x-hidden">
          <RulesDialogHeader
            title="Add rewards category"
            description="Add a new category to group criteria for reward rules."
            icon={Icons.addRewardsRule}
          />
          <div className="ml-[-26px] w-[105%]">
            <Divider />
          </div>

          <CategoryForm
            categoryDescription={categoryDescription}
            checkedMCCItems={checkedMCCItems}
            checkedMIDItems={checkedMIDItems}
            checkedCIDItems={checkedCIDItems}
            searchTermMCC={searchTermMCC}
            searchTermMID={searchTermMID}
            searchTermCID={searchTermCID}
            onCategoryDescriptionChange={handleCategoryDescriptionChange}
            onCheckboxChange={handleCheckboxChange}
            onSearchChangeMCC={handleSearchChangeMCC}
            onSearchChangeMID={handleSearchChangeMID}
            onSearchChangeCID={handleSearchChangeCID}
            onCancel={() => setOpen(false)}
            formik={formik}
          />

          <div className="ml-[-26px] w-[105%] mt-10">
            <Divider />
          </div>

          {/* Confirmation dialog */}
          <ConfirmationDialog
            isOpen={showConfirmation}
            onClose={handleCancel}
            onConfirm={() => {
              handleConfirm()
              resetCheckboxes()
              resetSearchTerms()
            }}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="outline"
                className="w-full md:w-[410px] h-[44px] mb-[-6px] text-[#344054] mt-1"
                onClick={resetSearchTerms}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              className="w-full md:w-[410px] h-[44px] mb-[-6px] mt-1 text-[#FFFFFF]"
              onClick={() => formik.handleSubmit()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CreateCategories
